import { yupResolver } from '@hookform/resolvers/yup';
import { GridRowParams } from '@material-ui/data-grid';
import DefaultLoader from '~/components/DefaultLoader';
import FormDefault from '~/components/FormDefault';
import { InputSelect, InputText } from '~/components/NovosInputs';
import Search from '~/components/Search';
import Separator from '~/components/Separator';
import ToggleDefault from '~/components/ToggleDefault';
import api from '~/services/api';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import AceEditor from 'react-ace';

import { ButtonCopiarConteudo, Container } from './styles';
import { GrupoAplicacao } from './types';
import { schema } from './validations';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { timezones } from './data';

type AnySelectOption<T = unknown> = Record<string, unknown> & {
  value: T;
  label: string;
};

type StatusOption = {
  value: number;
  label: string;
  des_status: string;
};

type GrupoAplicacaoOption = AnySelectOption<number> & {
  des_app_base_url?: string;
  des_api_base_url?: string;
  flg_inativo?: boolean;
};

const STATUS_OPTIONS: StatusOption[] = [
  {
    value: 0,
    label: 'Ativo',
    des_status:
      'Cliente em situação regular, utilizando os serviços normalmente.',
  },
  {
    value: 1,
    label: 'Período de Demonstração',
    des_status: 'Cliente em período de teste ativo.',
  },
  {
    value: 2,
    label: 'Período de Demonstração Expirado',
    des_status: 'Teste expirado, sem conversão para cliente regular.',
  },
  {
    value: 3,
    label: 'Inadimplente',
    des_status: 'Cliente com pendências financeiras.',
  },
  {
    value: 4,
    label: 'Suspenso',
    des_status: 'Serviços suspensos devido a inadimplência ou outras razões.',
  },
  {
    value: 5,
    label: 'Cancelado',
    des_status: 'Conta encerrada a pedido do cliente ou pela empresa.',
  },
  {
    value: 6,
    label: 'Aguardando Aprovação',
    des_status: 'Cliente recém-cadastrado aguardando validação ou ativação.',
  },
  {
    value: 7,
    label: 'Em Negociação',
    des_status:
      'Cliente em tratativas para resolução de pendências ou upgrade.',
  },
  {
    value: 8,
    label: 'Bloqueado',
    des_status:
      'Conta bloqueada por comportamento inadequado ou questões contratuais.',
  },
  {
    value: 9,
    label: 'Inativo',
    des_status: 'Conta inativa por decisão do cliente, mas ainda no sistema.',
  },
  {
    value: 10,
    label: 'Em Análise',
    des_status: 'Cadastro ou situação financeira sob revisão.',
  },
  {
    value: 11,
    label: 'Arquivado',
    des_status: 'Conta encerrada, mas com histórico preservado no sistema.',
  },
  {
    value: 12,
    label: 'Pré-Cadastro',
    des_status: 'Registro incompleto, aguardando finalização.',
  },
  {
    value: 13,
    label: 'Upgrade Solicitado',
    des_status: 'Cliente solicitou mudança de plano ou ampliação de serviços.',
  },
  {
    value: 14,
    label: 'Downgrade Solicitado',
    des_status: 'Cliente solicitou redução no plano ou serviços.',
  },
];

const SELECT_PLACEHOLDER: AnySelectOption<undefined> = {
  label: 'Selecione...',
  value: undefined,
};

const CadastroDeAplicacao: React.FC = () => {
  const MySwal = withReactContent(Swal);
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    resetField,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
  });

  const [loader, setLoader] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [flagInativo, setFlagInativo] = useState<boolean>(false);
  const [isOldInativo, setIsOldInativo] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(true);
  const [defaultOptions, setDefaultOptions] = useState<GrupoAplicacaoOption[]>(
    [],
  );
  const [planoContratacaoOptions, setPlanoContratacaoOptions] = useState<
    AnySelectOption<number>[]
  >([]);
  const [nomeEspaco, setNomeEspaco] = useState<string>('');
  const [desPayloadJson, setDesPayloadJson] = useState<string>('');

  useEffect(() => {
    const getGrupoAplicacao = async () => {
      const res = await api.get(`/grupo-de-aplicacao/`);
      const { success, data, message } = res.data;

      if (!success) throw new Error(message);

      const options = data.map((grupo: GrupoAplicacao) => ({
        label: grupo.des_nome,
        value: grupo.cod_grupo,
        des_app_base_url: grupo.des_app_base_url,
        des_api_base_url: grupo.des_api_base_url,
        flg_inativo: grupo.flg_inativo,
      }));

      setDefaultOptions(options);
    };
    getGrupoAplicacao();
  }, [isUpdate]);

  function findTextInputByName(name: string): HTMLInputElement | null {
    const textInputs = Array.from(
      document.querySelectorAll<HTMLInputElement>('input[type="text"]'),
    );

    return textInputs.find((input) => input.name === name) || null;
  }

  const focusDescricaoInput = () => {
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>(
        'input[name="des_nome"]',
      );
      input?.focus();
    });
  };

  useEffect(() => {
    const codGrupo = getValues('cod_grupo') as
      | AnySelectOption<number>
      | undefined;

    if (!codGrupo) return;

    const aplicacaoGrupo = defaultOptions.find(
      (item) => item.value === codGrupo.value,
    );

    const constructUrl = (baseUrl?: string) =>
      nomeEspaco
        ? `https://${nomeEspaco}.${baseUrl || ''}`.replace(/\.$/, '')
        : `https://${baseUrl || nomeEspaco}`;

    if (aplicacaoGrupo) {
      setValue('des_app_url', constructUrl(aplicacaoGrupo.des_app_base_url));
      setValue('des_api_url', constructUrl(aplicacaoGrupo.des_api_base_url));
    } else {
      setValue('des_app_url', constructUrl());
      setValue('des_api_url', constructUrl());
    }
  }, [nomeEspaco, defaultOptions, getValues, setValue]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/plano-contratacao`);
      if (data.success && data.data) {
        const options = data.data.map((plano: Record<string, unknown>) => ({
          ...plano,
          label: `${plano.cod_seq_plano} - ${plano.des_nome}`,
          value: plano.cod_seq_plano,
        })) as AnySelectOption<number>[];
        setPlanoContratacaoOptions(options);
      }
    })();
  }, []);

  const formBlank = {
    des_nome: '',
    des_nome_espaco: '',
    des_app_url: '',
    des_api_url: '',
    des_status_motivo: '',
    tipo_status_form: { ...SELECT_PLACEHOLDER },
    plano_contratacao: { ...SELECT_PLACEHOLDER },
    cod_seq_plano: null,
    flg_inativo: false,
    cod_grupo: { ...SELECT_PLACEHOLDER },
    timezone: '',
  };

  const onRowClick = async (param: GridRowParams) => {
    setLoader(true);
    const { row } = param;
    const {
      des_nome,
      des_nome_espaco,
      des_app_url,
      des_api_url,
      flg_inativo,
      cod_aplicacao,
      des_nome_grupo,
      cod_seq_plano,
      des_nome_plano,
      des_plano,
      timezone,
      des_payload_json,
    } = row;
    setDesPayloadJson(
      des_payload_json ? JSON.stringify(des_payload_json, null, 2) : '',
    );
    if (flg_inativo) setIsOldInativo(true);
    const findGrupo = defaultOptions.find(
      (item) => item.label === des_nome_grupo,
    );

    const findTimezone = timezones.find(
      (timezoneData) => timezoneData.value === timezone,
    );

    reset({
      des_nome,
      des_nome_espaco,
      des_app_url,
      des_api_url,
      flg_inativo,
    });

    if (cod_seq_plano) {
      setValue('plano_contratacao', {
        value: cod_seq_plano,
        label: `${cod_seq_plano} - ${des_nome_plano} (${des_plano})`,
      });
    }

    setValue('cod_grupo', {
      value: findGrupo?.value ?? undefined,
      label: findGrupo?.label ?? 'Selecione...',
    });
    setValue('timezone', {
      value: findTimezone?.value ?? undefined,
      label: findTimezone?.label ?? 'Selecione...',
    });

    setFlagInativo(flg_inativo);
    setValue('cod_aplicacao', cod_aplicacao);
    setUpdate(true);
    setValue('isUpdate', true);
    setShowSearch(false);
    setLoader(false);
  };

  const resetFormData = () => {
    setFlagInativo(false);
    reset(formBlank);
    setUpdate(false);
    setIsOldInativo(false);
    setValue('isUpdate', false);
    setDesPayloadJson('');
    setNomeEspaco('');
    setValue('des_app_url', '');
    setValue('des_api_url', '');
    setValue('plano_contratacao', { ...SELECT_PLACEHOLDER });

    setTimeout(() => {
      resetField('cod_grupo');
      setValue('cod_grupo', { ...SELECT_PLACEHOLDER });
      resetField('plano_contratacao');
      setValue('plano_contratacao', { ...SELECT_PLACEHOLDER });
      setValue('des_app_url', '');
      setValue('des_api_url', '');

      const inputCodGrupo = findTextInputByName('cod_grupo');
      if (inputCodGrupo) {
        inputCodGrupo.value = '';
        inputCodGrupo.placeholder = 'Selecione...';
      }

      const inputPlano = findTextInputByName('plano_contratacao');
      if (inputPlano) {
        inputPlano.value = '';
        inputPlano.placeholder = 'Selecione...';
      }
    }, 200);

    focusDescricaoInput();
  };

  const validateJSON = (jsonString: string) => {
    if (jsonString === null || jsonString.trim() === '') {
      return true;
    }

    try {
      const parsed = JSON.parse(jsonString);
      return typeof parsed === 'object' && !Array.isArray(parsed);
    } catch (error) {
      return false;
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(desPayloadJson);
      toast.success(
        'Conteúdo do payload copiado para a área de transferência!',
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      toast.warning(`Erro ao copiar texto${message ? `: ${message}` : ''}`);
    }
  };

  const onSubmit = handleSubmit(async (formValues) => {
    const { isUpdate: _ignored, ...rest } = formValues as Record<string, any>;
    const payload = { ...rest } as Record<string, any>;
    if (flagInativo && isUpdate && !isOldInativo) {
      const shouldContinue = await MySwal.fire({
        title: 'Informe o motivo de Inativação',
        html: (
          <Container>
            <Form>
              <Form.Group style={{ textAlign: 'left' }}>
                <InputSelect
                  label="Status"
                  placeholder="Selecione..."
                  maxLength={50}
                  name="tipo_status_form"
                  register={register}
                  isError={!!errors.tipo_status_form}
                  control={control}
                  options={STATUS_OPTIONS}
                  changeSelected={(selected: StatusOption) => {
                    setValue('tipo_status_form', selected);
                  }}
                />
              </Form.Group>
              <Form.Group style={{ textAlign: 'left' }}>
                <InputText
                  maxLength={100}
                  label="Motivo"
                  placeholder="Digite aqui o motivo da inativação"
                  name="des_status_motivo"
                  // caseInput="upper"
                  register={register}
                  control={control}
                  isError={!!errors.des_status_motivo}
                />
              </Form.Group>
            </Form>
          </Container>
        ),
        customClass: { actions: 'actions-modal' },
        width: 900,
        focusConfirm: false,
        showCloseButton: true,
        confirmButtonColor: '#0065FF',
        cancelButtonColor: '#ccc',
        showDenyButton: true,
        confirmButtonText: 'Sim',
        denyButtonText: 'Não',
        preConfirm: () => {
          return [
            getValues('des_status_motivo'),
            getValues('tipo_status_form'),
          ];
        },
      }).then(async (result) => {
        if (result.isConfirmed) {
          return true;
        }
        return false;
      });
      if (!shouldContinue) {
        return;
      }

      const des_status_motivo = getValues('des_status_motivo') as string;
      const tipo_status_form = getValues('tipo_status_form') as
        | StatusOption
        | undefined;

      payload.des_status_motivo = des_status_motivo;
      payload.tipo_status = tipo_status_form?.value;
    }
    if (!validateJSON(desPayloadJson))
      return toast.warning('O Payload informado não é um JSON válido.');
    const desPayloadJsonParsed =
      desPayloadJson.trim() === '' ? null : JSON.parse(desPayloadJson);
    if (isUpdate) {
      const res = await api.put(
        `/cadastro-de-aplicacao/${payload.cod_aplicacao}`,
        {
          ...payload,
          des_payload_json: desPayloadJsonParsed,
        },
      );

      const { success, message } = res.data;

      if (!success) {
        return toast.warning(
          message || 'Não foi possível atualizar a aplicação.',
        );
      }

      if (success) {
        resetFormData();
        setShowSearch(true);
        return toast.success(message || 'Aplicação atualizada com sucesso.');
      }
    }

    const res = await api.post('/cadastro-de-aplicacao', {
      ...payload,
      des_payload_json: desPayloadJsonParsed,
    });

    const { success, message } = res.data;

    if (!success) {
      return toast.warning(
        message || 'Não foi possível cadastrar a aplicação.',
      );
    }

    resetFormData();
    setShowSearch(false);
    return toast.success(message || 'Aplicação cadastrada com sucesso.');
  });

  const handleDesUrl = (selected: GrupoAplicacaoOption) => {
    const { des_nome_espaco } = getValues();

    const constructUrl = (baseUrl = '') =>
      des_nome_espaco
        ? `https://${des_nome_espaco.toLowerCase()}.${baseUrl}`
        : `https://${baseUrl}`;

    const newAppUrl = constructUrl(selected.des_app_base_url);
    const newApiUrl = constructUrl(selected.des_api_base_url);

    setValue('des_app_url', newAppUrl);
    setValue('des_api_url', newApiUrl);
  };

  const handleDelete = async () => {
    const { cod_aplicacao } = getValues();
    const res = await api.delete(`/cadastro-de-aplicacao/${cod_aplicacao}`);
    const { success, message } = res.data;
    if (!success) {
      return toast.warning(message || 'Não foi possível remover a aplicação.');
    }
    toast.success(message || 'Aplicação removida com sucesso.');
    resetFormData();
    setShowSearch(true);
  };

  const onNewData = () => {
    resetFormData();
    setShowSearch(false);
  };

  useEffect(() => {
    if (!showSearch && !isUpdate) {
      focusDescricaoInput();
    }
  }, [showSearch, isUpdate]);

  if (loader) {
    return (
      <Container>
        <DefaultLoader />
      </Container>
    );
  }

  return (
    <Container>
      {showSearch && (
        <Search codTela={201} newData={onNewData} onRowClick={onRowClick} />
      )}

      {!showSearch && (
        <FormDefault
          codTela={201}
          title="Cadastro de Aplicação"
          codigoRegistro={[
            { value: getValues('cod_aplicacao'), des_campo: 'Código' },
          ]}
          onSave={async () => {
            await onSubmit();
          }}
          onCancel={() => {
            resetFormData();
            setShowSearch(true);
          }}
          isUpdate={isUpdate}
          onNew={resetFormData}
          onDelete={handleDelete}
          onClearFields={resetFormData}
          onReturnSearch={() => {
            resetFormData();
            setShowSearch(true);
          }}
        >
          <Row>
            <Col sm={12} md={6}>
              <InputText
                maxLength={100}
                label="Descrição"
                placeholder="Digite aqui a Descrição"
                name="des_nome"
                caseInput="upper"
                register={register}
                control={control}
                isError={!!errors.des_nome}
              />
            </Col>
            <Col sm={12} md={3}>
              <InputText
                maxLength={50}
                label="Nome do Espaço"
                placeholder="Informe o Nome do Espaço"
                control={control}
                name="des_nome_espaco"
                register={register}
                isError={!!errors.des_nome_espaco}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const lastLetter = event.target.value.slice(-1);
                  const regex = /^[a-zA-Z0-9]+$/;
                  if (regex.test(lastLetter) || lastLetter === '') {
                    setValue(
                      'des_nome_espaco',
                      event.target.value.toLowerCase(),
                    );
                    setNomeEspaco(event.target.value);
                  }
                }}
              />
            </Col>
            <Col sm={12} md={2}>
              <InputSelect
                label="Plano de Contratação"
                maxLength={50}
                placeholder="Selecione..."
                name="plano_contratacao"
                options={planoContratacaoOptions}
                register={register}
                isError={!!errors.plano_contratacao}
                control={control}
                disabled={false}
                changeSelected={(selected) => {
                  setValue('plano_contratacao', selected);
                  setValue('cod_seq_plano', selected.value);
                }}
              />
            </Col>
            <Col sm={12} md={1}>
              <ToggleDefault
                labelText="Inativo?"
                setChecked={flagInativo}
                onSwitch={() => {
                  setValue('flg_inativo', !flagInativo);
                  setFlagInativo(!flagInativo);
                }}
              />
            </Col>
          </Row>
          <Row className="d-flex align-items-center">
            <Col sm={12} md={3}>
              <InputSelect
                label="Grupo de Aplicação"
                placeholder="Selecione..."
                maxLength={50}
                name="cod_grupo"
                register={register}
                isError={!!errors.cod_grupo}
                control={control}
                options={defaultOptions.filter(
                  (option) => option.flg_inativo !== true,
                )}
                changeSelected={(selected: GrupoAplicacaoOption) => {
                  setValue('cod_grupo', selected);
                  clearErrors('cod_grupo');
                  handleDesUrl(selected);
                }}
              />
            </Col>
            <Col sm={12} md={3}>
              <InputSelect
                label="Fuso Horário"
                placeholder="Selecione..."
                name="timezone"
                options={timezones}
                register={register}
                isError={!!errors.timezone}
                control={control}
                changeSelected={(value: AnySelectOption<string>) => {
                  setValue('timezone', value);
                  clearErrors('timezone');
                }}
              />
            </Col>
          </Row>

          <Col
            sm={12}
            md={8}
            className="url-content"
            style={{ color: '#A5ADBA' }}
          >
            <InputText
              maxLength={1000}
              label="URL Aplicação"
              placeholder=""
              disabled
              name="des_app_url"
              register={register}
              control={control}
              isError={!!errors.des_app_url}
            />
            <InputText
              maxLength={1000}
              label="URL API"
              placeholder=""
              disabled
              name="des_api_url"
              register={register}
              control={control}
              isError={!!errors.des_api_url}
            />
          </Col>
          <Separator labelText="Payload (JSON)" childrenWidth="120px">
            <ButtonCopiarConteudo
              type="button"
              style={{
                marginTop: '-7px',
              }}
              onClick={() => handleCopy()}
            >
              Copiar conteúdo
            </ButtonCopiarConteudo>
          </Separator>
          <Col sm={12} md={12} className="mt-2">
            <AceEditor
              placeholder=""
              mode="json"
              theme="monokai"
              name="des_payload_json"
              fontSize={14}
              showPrintMargin
              showGutter
              highlightActiveLine
              value={desPayloadJson}
              height="380px"
              width="100%"
              setOptions={{
                showPrintMargin: false,
                enableBasicAutocompletion: false,
                enableLiveAutocompletion: false,
                enableSnippets: false,
                showLineNumbers: true,
                tabSize: 2,
                setShowPrintMargin: false,
                useWorker: false,
              }}
              onChange={(value) => {
                setDesPayloadJson(value);
              }}
            />
          </Col>
        </FormDefault>
      )}
    </Container>
  );
};

export default CadastroDeAplicacao;
